/**
 * Responses operations — background query submission and retrieval.
 *
 * submitBackgroundQuery: POST /v1/responses with background=true → 202 with task ID.
 * getResponse: GET /v1/responses/{responseId} → current status + output.
 * extractResponseText: Walk response output and join text content.
 */

import type { components } from '@arbidocs/client'
import { authenticatedFetch, type AuthHeaders } from '../fetch.js'

type MessageInput = components['schemas']['MessageInput']
type ResponsesAPIResponse = components['schemas']['ResponsesAPIResponse']
type ToolEntry = NonNullable<MessageInput['tools']>[string]

// ── Submit background query ──────────────────────────────────────────────────

export interface SubmitBackgroundQueryOptions extends AuthHeaders {
  workspaceId: string
  question: string
  docIds: string[]
  previousResponseId?: string | null
  model?: string
}

/**
 * Submit a query for background processing.
 * Returns immediately with a response ID and "queued" status.
 */
export async function submitBackgroundQuery(
  options: SubmitBackgroundQueryOptions
): Promise<ResponsesAPIResponse> {
  const { workspaceId, question, docIds, previousResponseId, model, ...auth } = options

  const tools: Record<string, ToolEntry> = {}
  if (docIds.length > 0) {
    tools.retrieval_chunk = {
      name: 'retrieval_chunk',
      description: 'retrieval chunk',
      tool_args: { doc_ext_ids: docIds },
      tool_responses: {},
    }
    tools.retrieval_full_context = {
      name: 'retrieval_full_context',
      description: 'retrieval full context',
      tool_args: { doc_ext_ids: [] },
      tool_responses: {},
    }
  }

  const body: MessageInput = {
    input: question,
    workspace_ext_id: workspaceId,
    stream: false,
    background: true,
    store: true,
    tools,
    ...(previousResponseId ? { previous_response_id: previousResponseId } : {}),
    ...(model ? { model } : {}),
  }

  const res = await authenticatedFetch({
    ...auth,
    path: '/v1/responses',
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  })

  return (await res.json()) as ResponsesAPIResponse
}

// ── Get response status/result ───────────────────────────────────────────────

/**
 * Fetch the current state of a response by ID.
 * Returns progressive status, output, usage, and metadata.
 */
export async function getResponse(
  auth: AuthHeaders,
  responseId: string
): Promise<ResponsesAPIResponse> {
  const res = await authenticatedFetch({
    ...auth,
    path: `/v1/responses/${responseId}`,
    method: 'GET',
  })

  return (await res.json()) as ResponsesAPIResponse
}

// ── Text extraction ──────────────────────────────────────────────────────────

/**
 * Walk response output messages and join all output_text content.
 * Pure function, no I/O.
 */
export function extractResponseText(response: ResponsesAPIResponse): string {
  const parts: string[] = []
  for (const msg of response.output) {
    for (const item of msg.content) {
      if ('type' in item && item.type === 'output_text' && 'text' in item) {
        parts.push(item.text as string)
      }
    }
  }
  return parts.join('')
}
